import { providers } from '../../providers';
import { baidu } from '../baidu';
import { kie } from '../kie';
import { wavespeed } from '../wavespeed';
import { type VideoModelEntry } from '../types';
import { getUiOptionsForModel } from '../ui-options';

// 价格表: 人民币/秒
const KLING_V30 = { '720p': 0.6, '1080p': 0.8, '4k': 3 };
const KLING_V3_TURBO = { '720p': 0.8, '1080p': 1.0 };
const KLING_V30_SOUND = { '720p': 0.9, '1080p': 1.2, '4k': 3 };
const KLING_O3 = { '720p': 0.6, '1080p': 0.8, '4k': 3 };
const KLING_O3_SOUND = { '720p': 0.8, '1080p': 1, '4k': 3 };
const KLING_O3_V2V = { '720p': 0.9, '1080p': 1.2};
const KLING_V26 = { '1080p': 0.5 };
const KLING_V26_SOUND = { '1080p': 1 };
const KLING_V25 = { '720p': 0.3, '1080p': 0.5 };
const KLING_AVATAR = { '720p': 0.4, '1080p': 0.8 };
// 查找价格函数
const lookup = (table: Record<string, number>, quality?: string) =>
    table[quality ?? ''] ?? Math.max(...Object.values(table));
// 美元汇率
const RMB_TO_USD = 1 / 6.8;
// rmb to usd 转换
const usd = (rmb: number) => rmb * RMB_TO_USD;

const COST_FACTOR = 1.5;


export const klingModels: VideoModelEntry[] = [
    {
        id: 'kling-v3-turbo',
        chef: providers['kling'],
        providers: [
            {
                ...providers.kling,
                model: kie('kling-v3-turbo'),
                modelBackup: [wavespeed('kling-v3-turbo')],
                getCost: ({ duration, quality }) => usd(duration * lookup(KLING_V3_TURBO, quality)) * COST_FACTOR,
                getPassCount: ({ duration, quality }) => usd(duration * lookup(KLING_V3_TURBO, quality)) * COST_FACTOR * 100,
            },
        ],
        configByMode: {
            none: {
                aspectRatios: ['16:9', '9:16', '1:1'],
                durations: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['720p', '1080p'],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: [],
                durations: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['720p', '1080p'],
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
        },
        supportsEdit: false,
        supportsInput: ['t2v', 'first'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
    {
        id: 'kling-v3.0',
        chef: providers['kling'],
        providers: [
            {
                ...providers.kling,
                model: baidu('kling-v3.0'),
                getCost: ({ duration, quality, providerOptions }) => {
                    const withSound = !!providerOptions?.sound;
                    return usd(duration * lookup(withSound ? KLING_V30_SOUND : KLING_V30, quality) * COST_FACTOR);
                },
                getPassCount: ({ duration, quality, providerOptions }) => {
                    const withSound = !!providerOptions?.sound;
                    return usd(duration * lookup(withSound ? KLING_V30_SOUND : KLING_V30, quality)) * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            none: {
                aspectRatios: ['16:9', '9:16', '1:1'],
                durations: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['720p', '1080p', '4k'],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: [],
                durations: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['720p', '1080p', '4k'],
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first_last: {
                aspectRatios: ['16:9', '9:16', '1:1'],
                durations: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['720p', '1080p', '4k'],
                inlineMedia: {
                    image: { allow: true, maxCount: 2, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
            first_last: ['prompt', 'image2'],
        },
        supportsEdit: false,
        supportsInput: ['t2v', 'first', 'first_last'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
        uiOptions: getUiOptionsForModel('kling-v3.0'),
    },
    {
        id: 'kling-v3.0-omni',
        chef: providers['kling'],
        providers: [
            {
                ...providers.kling,
                model: baidu('kling-v3.0-omni'),
                getCost: ({ duration, quality, providerOptions }) => {
                    const withSound = !!providerOptions?.sound;
                    return usd(duration * lookup(withSound ? KLING_O3_SOUND : KLING_O3, quality)) * COST_FACTOR;
                },
                getPassCount: ({ duration, quality, providerOptions }) => {
                    const withSound = !!providerOptions?.sound;
                    return usd(duration * lookup(withSound ? KLING_O3_SOUND : KLING_O3, quality)) * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            none: {
                aspectRatios: ['16:9', '9:16', '1:1'],
                durations: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['720p', '1080p', '4k'],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: [],
                durations: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['720p', '1080p', '4k'],
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first_last: {
                aspectRatios: ['16:9', '9:16', '1:1'],
                durations: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['720p', '1080p', '4k'],
                inlineMedia: {
                    image: { allow: true, maxCount: 2, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            ref: {
                aspectRatios: ['16:9', '9:16', '1:1'],
                durations: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['720p', '1080p', '4k'],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 7, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
            first_last: ['prompt', 'image2'],
            ref: ['prompt', 'image'],
        },
        supportsEdit: false,
        supportsInput: ['t2v', 'first', 'first_last', 'reference'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
        uiOptions: getUiOptionsForModel('kling-v3.0-omni'),
    },
    {
        id: 'kling-v3.0-omni-v2v',
        chef: providers['kling'],
        providers: [
            {
                ...providers.kling,
                model: baidu('kling-v3.0-omni-v2v'),
                getCost: ({ duration, quality, providerOptions }) => {
                    const inputDur = providerOptions?._media?.videoDurationSeconds ?? duration ?? 10;
                    return usd(inputDur * lookup(KLING_O3_V2V, quality)) * COST_FACTOR;
                },
                getPassCount: ({ duration, quality, providerOptions }) => {
                    const inputDur = providerOptions?._media?.videoDurationSeconds ?? duration ?? 10;
                    return usd(inputDur * lookup(KLING_O3_V2V, quality)) * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            v2v: {
                aspectRatios: [],
                durations: [],
                qualities: ['720p', '1080p'],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: true, maxCount: 1, maxSize: '200MB', minDuration: '3s', maxDuration: '15s', minPixels: 518400, maxPixels: 4665600, formats: ['video/mp4', 'video/mov'] },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            v2v: ['prompt', 'video'],
        },
        supportsEdit: true,
        supportsInput: ['v2v'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
        uiOptions: getUiOptionsForModel('kling-v3.0-omni-v2v'),
    },
    {
        id: 'kling-v2.6',
        chef: providers['kling'],
        providers: [
            {
                ...providers.kling,
                model: baidu('kling-v2.6'),
                getCost: ({ duration, quality, providerOptions }) => {
                    const withSound = !!providerOptions?.sound;
                    return usd(duration * lookup(withSound ? KLING_V26_SOUND : KLING_V26, quality)) * COST_FACTOR;
                },
                getPassCount: ({ duration, quality, providerOptions }) => {
                    const withSound = !!providerOptions?.sound;
                    return usd(duration * lookup(withSound ? KLING_V26_SOUND : KLING_V26, quality)) * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            none: {
                aspectRatios: ['16:9', '9:16', '1:1'],
                durations: [5, 10],
                qualities: ['1080p'],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: [],
                durations: [5, 10],
                qualities: ['1080p'],
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first_last: {
                aspectRatios: ['16:9', '9:16', '1:1'],
                durations: [5, 10],
                qualities: ['1080p'],
                inlineMedia: {
                    image: { allow: true, maxCount: 2, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
            first_last: ['prompt', 'image2'],
        },
        supportsEdit: false,
        supportsInput: ['t2v', 'first', 'first_last'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
        uiOptions: getUiOptionsForModel('kling-v2.6'),
    },
    {
        id: 'kling-v2.5',
        chef: providers['kling'],
        providers: [
            {
                ...providers.kling,
                model: baidu('kling-v2.5'),
                getCost: ({ duration, quality }) => usd(duration * lookup(KLING_V25, quality)) * COST_FACTOR,
                getPassCount: ({ duration, quality }) => usd(duration * lookup(KLING_V25, quality)) * COST_FACTOR * 100,
            },
        ],
        configByMode: {
            none: {
                aspectRatios: ['16:9', '9:16', '1:1'],
                durations: [5, 10],
                qualities: ['720p', '1080p'],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: [],
                durations: [5, 10],
                qualities: ['720p', '1080p'],
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first_last: {
                aspectRatios: ['16:9', '9:16', '1:1'],
                durations: [5, 10],
                qualities: ['1080p'],
                inlineMedia: {
                    image: { allow: true, maxCount: 2, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
            first_last: ['prompt', 'image2'],
        },
        supportsEdit: false,
        supportsInput: ['t2v', 'first', 'first_last'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
        uiOptions: getUiOptionsForModel('kling-v2.5'),
    },
    {
        id: 'kling-v2-avatar',
        chef: providers['kling'],
        providers: [
            {
                ...providers.kling,
                model: baidu('kling-v2-avatar'),
                getCost: ({ quality, providerOptions }) => {
                    const inputDur = providerOptions?._media?.audioDurationSeconds ?? 15;
                    return usd(inputDur * lookup(KLING_AVATAR, quality) * COST_FACTOR);
                },
                getPassCount: ({ quality, providerOptions }) => {
                    const inputDur = providerOptions?._media?.audioDurationSeconds ?? 15;
                    return usd(inputDur * lookup(KLING_AVATAR, quality) * COST_FACTOR) * 100;
                },
            },
        ],
        configByMode: {
            avatar: {
                aspectRatios: [],
                durations: [],
                qualities: ['720p', '1080p'],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 1, maxSize: '10MB', slot: true, formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false, maxCount: 0, maxSize: '0' },
                    audio: {
                        allow: true,
                        maxCount: 1,
                        maxSize: '5MB',
                        minDuration: '2s',
                        maxDuration: '300s',
                        slot: true,
                        formats: ['audio/mpeg', 'audio/wav', 'audio/mp4', 'audio/aac'],
                    },
                    maxTotalCount: 2,
                },
            },
        },
        precheckByMode: {
            avatar: ['image', 'audio'],
        },
        supportsEdit: false,
        supportsInput: ['avatar'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
        tags: ['avatar'],
    },
];